
import {
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faArrowsRotate,
  faRotateLeft,
  faRotateRight,
  faSearchMinus,
  faSearchPlus,
} from '@fortawesome/free-solid-svg-icons';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { swalNotification } from '@/core/helpers/utils';

export default defineComponent({
  name: 'avatar-crop-modal',

  components: {
    ButtonCustom,
    FontAwesomeIcon,
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    imageSrc: {
      type: String,
      default: '',
    },
    fileName: {
      type: String,
      default: 'avatar.jpg',
    },
    mimeType: {
      type: String,
      default: 'image/jpeg',
    },
    maxSizeMb: {
      type: Number,
      default: 2,
    },
  },

  emits: ['update:modelValue', 'confirm', 'cancel'],

  setup(props, { emit }) {
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const visible = ref(false);
    const imageRef = ref<HTMLImageElement | null>(null);
    const previewRef = ref<HTMLElement | null>(null);
    const cropperReady = ref(false);
    const confirming = ref(false);
    const dialogOpened = ref(false);

    let cropper: Cropper | null = null;
    let initToken = 0;

    const destroyCropper = () => {
      if (cropper) {
        try {
          cropper.destroy();
        } catch {
          // Cropper may already be torn down with the dialog DOM.
        }
        cropper = null;
      }
      cropperReady.value = false;
    };

    const isImageReady = (img: HTMLImageElement) =>
      img.complete && img.naturalWidth > 0;

    const createCropper = (token: number) => {
      const img = imageRef.value;
      if (!img || !props.imageSrc || token !== initToken) {
        return;
      }
      if (!isImageReady(img)) {
        return;
      }

      destroyCropper();

      cropper = new Cropper(img, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        responsive: true,
        background: false,
        guides: true,
        center: true,
        highlight: false,
        checkOrientation: false,
        rotatable: true,
        scalable: true,
        zoomable: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        preview: previewRef.value || undefined,
        ready() {
          if (token !== initToken) {
            return;
          }
          cropperReady.value = true;
        },
      });
    };

    const assignImageSrc = async () => {
      await nextTick();
      const img = imageRef.value;
      if (!img || !props.imageSrc) {
        return;
      }

      // Setting src imperatively avoids Vue re-patching Cropper's wrapped node.
      if (img.getAttribute('src') !== props.imageSrc) {
        img.src = props.imageSrc;
      }

      if (isImageReady(img)) {
        createCropper(initToken);
      }
    };

    const onImageLoad = () => {
      if (!dialogOpened.value || !visible.value) {
        return;
      }
      createCropper(initToken);
    };

    const onDialogOpened = async () => {
      dialogOpened.value = true;
      initToken += 1;
      await assignImageSrc();
    };

    const close = () => {
      visible.value = false;
      emit('update:modelValue', false);
      emit('cancel');
    };

    const onClosed = () => {
      initToken += 1;
      dialogOpened.value = false;
      destroyCropper();
      confirming.value = false;
      if (imageRef.value) {
        imageRef.value.removeAttribute('src');
      }
    };

    const withReadyCropper = (action: (instance: Cropper) => void) => {
      if (!cropper || !cropperReady.value) {
        return;
      }
      try {
        action(cropper);
      } catch {
        // Ignore actions while Cropper is mid-destroy / not ready.
      }
    };

    const zoom = (ratio: number) => {
      withReadyCropper((instance) => instance.zoom(ratio));
    };

    const rotate = (degree: number) => {
      withReadyCropper((instance) => instance.rotate(degree));
    };

    const reset = () => {
      withReadyCropper((instance) => instance.reset());
    };

    const canvasToFile = (canvas: HTMLCanvasElement): Promise<File> => {
      const preferredType = props.mimeType.startsWith('image/')
        ? props.mimeType
        : 'image/jpeg';
      const outputType =
        preferredType === 'image/png' || preferredType === 'image/webp'
          ? preferredType
          : 'image/jpeg';
      const quality = outputType === 'image/jpeg' ? 0.92 : undefined;

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Không thể tạo ảnh đã cắt'));
              return;
            }
            const extension =
              outputType === 'image/png'
                ? 'png'
                : outputType === 'image/webp'
                  ? 'webp'
                  : 'jpg';
            const baseName = props.fileName.replace(/\.[^.]+$/, '') || 'avatar';
            resolve(
              new File([blob], `${baseName}.${extension}`, {
                type: outputType,
                lastModified: Date.now(),
              })
            );
          },
          outputType,
          quality
        );
      });
    };

    const confirm = async () => {
      if (!cropper || !cropperReady.value) {
        await swalNotification(
          'Ảnh chưa sẵn sàng để cắt, vui lòng chờ giây lát',
          'warning'
        );
        return;
      }

      try {
        confirming.value = true;
        const canvas = cropper.getCroppedCanvas({
          width: 512,
          height: 512,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });

        if (!canvas) {
          await swalNotification('Không thể cắt ảnh, vui lòng thử lại', 'warning');
          return;
        }

        let file = await canvasToFile(canvas);
        const maxBytes = props.maxSizeMb * 1024 * 1024;

        if (file.size > maxBytes && file.type !== 'image/jpeg') {
          const jpegCanvas = cropper.getCroppedCanvas({
            width: 512,
            height: 512,
          });
          if (!jpegCanvas) {
            await swalNotification('Không thể nén ảnh, vui lòng thử lại', 'warning');
            return;
          }
          file = await new Promise<File>((resolve, reject) => {
            jpegCanvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Không thể nén ảnh'));
                  return;
                }
                resolve(
                  new File([blob], 'avatar.jpg', {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  })
                );
              },
              'image/jpeg',
              0.85
            );
          });
        }

        if (file.size > maxBytes) {
          await swalNotification(
            `Ảnh sau khi cắt vượt quá ${props.maxSizeMb}MB, vui lòng thu nhỏ vùng cắt`,
            'warning'
          );
          return;
        }

        emit('confirm', file);
        visible.value = false;
        emit('update:modelValue', false);
      } catch {
        await swalNotification('Không thể xử lý ảnh, vui lòng thử lại', 'error');
      } finally {
        confirming.value = false;
      }
    };

    watch(
      () => props.modelValue,
      (open) => {
        visible.value = open;
        if (!open) {
          destroyCropper();
        }
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      initToken += 1;
      destroyCropper();
    });

    return {
      ButtonTypeColors,
      ButtonsType,
      close,
      confirm,
      confirming,
      cropperReady,
      faArrowsRotate,
      faRotateLeft,
      faRotateRight,
      faSearchMinus,
      faSearchPlus,
      imageRef,
      onClosed,
      onDialogOpened,
      onImageLoad,
      previewRef,
      reset,
      rotate,
      visible,
      zoom,
    };
  },
});
